import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useCiAuthStore } from './ciAuthStore';

/**
 * Page d'authentification DÉDIÉE au Community Intelligence Dashboard.
 *
 * Réutilise la RPC `login_user` de l'application (mêmes credentials que
 * DynastyPool) mais avec une UI et une session séparées. La session pool
 * (useUserStore) n'est jamais modifiée par ce formulaire.
 */
export function CiAuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useCiAuthStore((s) => s.setUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: string } | null)?.from || '/ci-dashboard';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('login_user', {
        p_email: email.trim(),
        p_password: password,
      });
      if (rpcError) {
        setError('Erreur de connexion. Réessayez plus tard.');
        return;
      }
      const user = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (!user || !user.user_id) {
        setError('Identifiants invalides.');
        return;
      }
      setUser({
        user_id: Number(user.user_id),
        username: String(user.username ?? ''),
        email: String(user.email ?? email.trim()),
      });
      navigate(from, { replace: true });
    } catch {
      setError('Erreur inattendue lors de la connexion.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ci-dashboard">
      <div className="ci-auth">
        <form className="ci-auth__card" onSubmit={onSubmit} noValidate>
          <h1 className="ci-auth__title">Community Intelligence Dashboard</h1>
          <p className="ci-auth__subtitle">
            Connexion réservée aux opérateurs de pool autorisés. Utilisez vos identifiants
            DynastyPool habituels.
          </p>

          {error && <div className="ci-error">{error}</div>}

          <div className="ci-field">
            <label htmlFor="ci-email">Courriel</label>
            <input
              id="ci-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="ci-field">
            <label htmlFor="ci-password">Mot de passe</label>
            <input
              id="ci-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="ci-btn" disabled={loading || !email || !password}>
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}
